// --- React ---
import React, { useState, useEffect, useRef } from 'react';

// --- 다국어 ---
import { useTranslation } from 'react-i18next';

// --- 스타일 · 테마 ---
import styled from '@emotion/styled';
import { useTheme } from '@/theme';

// --- 공통 UI 컴포넌트 ---
import {
  Card,
  Text,
  ButtonTimeLine,
  ButtonTimeLineGroup,
  CardTitle,
  Flex,
} from '@/components/ui';

// --- 유틸 ---
import { formatTime } from '@/utils/date';

// --- i18n ---
import { i18nKeys } from '@/i18n/i18nKeyGen';

// --- 외부 라이브러리 ---
import DatePicker, { registerLocale } from 'react-datepicker';
import { ko } from 'date-fns/locale/ko';
import { enUS } from 'date-fns/locale/en-US';
import 'react-datepicker/dist/react-datepicker.css';

registerLocale('ko', ko);
registerLocale('en', enUS);

// ==================== 스타일 컴포넌트 ====================

const DatePickerWrapper = styled.div`
  .react-datepicker-wrapper {
    width: auto;
  }

  .react-datepicker__input-container input {
    width: 130px;
    height: 26px;
    padding: 4px 12px;
    border: 1px solid ${({ theme }) => theme.colors.border.interactive.default};
    border-radius: ${({ theme }) => theme.borderRadius.md}px;
    background: ${({ theme }) => theme.colors.surface.control.default};
    font-size: ${({ theme }) => theme.fontSize.md}px;
    color: ${({ theme }) => theme.colors.text.primary};
    text-align: center;
    cursor: pointer;
    outline: none;
    transition: border-color 0.2s ease;
    box-sizing: border-box;

    &:hover {
      border-color: ${({ theme }) => theme.colors.border.interactive.hover};
    }

    &:focus {
      border-color: ${({ theme }) => theme.colors.border.indicator};
    }

    &:disabled {
      background: ${({ theme }) => theme.colors.surface.control.disabled};
      color: ${({ theme }) => theme.colors.text.disabled};
      cursor: not-allowed;
    }
  }

  .react-datepicker {
    font-family: inherit;
    border: 1px solid ${({ theme }) => theme.colors.border.interactive.default};
    border-radius: ${({ theme }) => theme.borderRadius.md}px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .react-datepicker__header {
    background: ${({ theme }) => theme.colors.surface.control.emphasis};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.interactive.default};
  }

  .react-datepicker__day--selected {
    background-color: ${({ theme }) => theme.colors.surface.group.brand};
    border-radius: ${({ theme }) => theme.borderRadius.sm}px;
  }

  .react-datepicker__day--disabled {
    color: ${({ theme }) => theme.colors.text.disabled};
    cursor: not-allowed;
  }

  .react-datepicker__day:hover:not(.react-datepicker__day--disabled) {
    background-color: ${({ theme }) => theme.colors.surface.interactive.hover};
    border-radius: ${({ theme }) => theme.borderRadius.sm}px;
  }

  .react-datepicker__month-dropdown-container,
  .react-datepicker__year-dropdown-container {
    margin: 0 4px;
  }

  .react-datepicker__month-dropdown-container select,
  .react-datepicker__year-dropdown-container select {
    padding: 2px 4px;
    border: 1px solid ${({ theme }) => theme.colors.border.interactive.default};
    border-radius: ${({ theme }) => theme.borderRadius.sm}px;
    background: ${({ theme }) => theme.colors.surface.control.default};
    color: ${({ theme }) => theme.colors.text.primary};
    font-size: ${({ theme }) => theme.fontSize.sm}px;
    cursor: pointer;
    outline: none;

    &:focus {
      border-color: ${({ theme }) => theme.colors.border.indicator};
    }
  }
`;

// ==================== 타입 정의 ====================

interface MonitoringDataPoint {
  created_at: string;
  [key: string]: any;
}

interface MonitoringSliderProps {
  /** 모니터링 데이터 배열 (시간순 정렬 필요) */
  monitoringData: MonitoringDataPoint[];
  /** 선택된 데이터 포인트 변경 콜백 */
  onDataPointChange: (dataPoint: MonitoringDataPoint) => void;
  /** 자동 재생 여부 (기본값: true) */
  autoPlay?: boolean;
  /** 재생 속도 (배속, 기본값: 1) */
  initialPlaybackSpeed?: number;
  /** 날짜 선택 값 (옵션) */
  selectedDate?: string;
  /** 날짜 변경 콜백 (옵션) */
  onDateChange?: (date: string) => void;
  /** 데이터가 존재하는 날짜 목록 (YYYY-MM-DD[]) */
  availableDates?: string[];
  /** 날짜 목록 로딩 중 */
  isLoadingDates?: boolean;
  /** 조회 중 상태 (옵션) */
  isQuerying?: boolean;
}

// ==================== 컴포넌트 ====================

/**
 * 모니터링 데이터 타임라인 슬라이더
 * - 시간 범위 기반 슬라이더
 * - 자동 재생 기능
 * - 재생/일시정지 토글
 * - 배속 조절 (0.5x, 1x, 2x, 4x)
 */
export const MonitoringSlider: React.FC<MonitoringSliderProps> = ({
  monitoringData,
  onDataPointChange,
  autoPlay = true,
  initialPlaybackSpeed = 1,
  selectedDate,
  onDateChange,
  availableDates = [],
  isLoadingDates = false,
  isQuerying = false,
}) => {
  const { theme } = useTheme();
  const { i18n, t } = useTranslation('monitoring');

  // ==================== 상태 관리 ====================

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [playbackSpeed, setPlaybackSpeed] = useState(initialPlaybackSpeed);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // ==================== 파생 데이터 ====================

  const hasData = monitoringData && monitoringData.length > 0;

  // 시간 범위 계산
  const timestamps = hasData ? monitoringData.map(d => new Date(d.created_at).getTime()) : [];
  const minTime = timestamps.length > 0 ? Math.min(...timestamps) : 0;
  const maxTime = timestamps.length > 0 ? Math.max(...timestamps) : 0;

  // 현재 선택된 데이터 포인트
  const currentDataPoint = hasData ? monitoringData[currentIndex] : null;
  const currentTime = currentDataPoint ? new Date(currentDataPoint.created_at) : new Date();

  // ==================== 핸들러 ====================

  /** 슬라이더 수동 조작 (재생 중지) */
  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newIndex = parseInt(e.target.value, 10);
    setCurrentIndex(newIndex);
    setIsPlaying(false);
  };

  /** 재생/일시정지 토글 */
  const togglePlayPause = () => {
    setIsPlaying(prev => !prev);
  };

  /** 배속 순환 변경 (0.5x → 1x → 2x → 4x → 0.5x) */
  const handleSpeedChange = () => {
    setPlaybackSpeed(prev => {
      const speeds: (0.5 | 1 | 2 | 4)[] = [0.5, 1, 2, 4];
      const currentSpeedIndex = speeds.indexOf(prev as 0.5 | 1 | 2 | 4);
      const nextSpeedIndex = (currentSpeedIndex + 1) % speeds.length;
      return speeds[nextSpeedIndex];
    });
  };

  /** 이전 데이터 포인트로 이동 */
  const handlePrevious = () => {
    setCurrentIndex(prev => Math.max(0, prev - 1));
    setIsPlaying(false);
  };

  /** 다음 데이터 포인트로 이동 */
  const handleNext = () => {
    setCurrentIndex(prev => Math.min(monitoringData.length - 1, prev + 1));
    setIsPlaying(false);
  };

  // ==================== 이펙트 ====================

  /** currentIndex 변경 시 부모에 데이터 포인트 콜백 */
  useEffect(() => {
    if (monitoringData[currentIndex]) {
      onDataPointChange(monitoringData[currentIndex]);
    }
  }, [currentIndex]);

  /**
   * 자동 재생 로직.
   * 배속에 따라 인터벌을 조정하고, 마지막에 도달하면 재생 중지.
   */
  useEffect(() => {
    if (isPlaying) {
      const interval = 1000 / playbackSpeed;

      intervalRef.current = setInterval(() => {
        setCurrentIndex(prev => {
          const nextIndex = prev + 1;
          if (nextIndex >= monitoringData.length) {
            setIsPlaying(false);
            return prev;
          }
          return nextIndex;
        });
      }, interval);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [isPlaying, playbackSpeed, monitoringData.length]);

  /** 데이터 변경 시 인덱스 초기화 및 자동 재생 시작 */
  useEffect(() => {
    setCurrentIndex(0);
    setIsPlaying(autoPlay);
  }, [monitoringData, autoPlay]);

  // ==================== 렌더링 ====================

  return (
    <Card padding={6} layout="flex" gap={4} data-tour="playback-timeline">
      <Flex direction="column" gap={5} align="stretch">
        {/* 날짜 선택 (데이터 있는 날짜만 활성화) */}
        <Flex direction="row" justify="between" align="center">
          <CardTitle>Time line</CardTitle>
          <Flex direction="row" gap={4} justify="end" align="center">
            {isQuerying && (
              <Text size="sm" style={{ color: theme.colors.text.secondary }}>
                {t(i18nKeys.monitoring.playback_querying)}
              </Text>
            )}
            <DatePickerWrapper>
              <DatePicker
                selected={selectedDate ? new Date(selectedDate + 'T00:00:00') : null}
                onChange={(date: Date | null) => {
                  if (date) {
                    const yyyy = date.getFullYear();
                    const mm = String(date.getMonth() + 1).padStart(2, '0');
                    const dd = String(date.getDate()).padStart(2, '0');
                    onDateChange?.(`${yyyy}-${mm}-${dd}`);
                  }
                }}
                includeDates={availableDates.map(d => new Date(d + 'T00:00:00'))}
                locale={i18n.language.startsWith('ko') ? 'ko' : 'en'}
                dateFormat="yyyy.MM.dd"
                placeholderText={t(i18nKeys.monitoring.playback_select_date)}
                disabled={isLoadingDates || availableDates.length === 0}
                showPopperArrow={false}
                popperPlacement="bottom-end"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
              />
            </DatePickerWrapper>
          </Flex>
        </Flex>

        {/* 슬라이더 영역 */}
        {monitoringData && monitoringData.length > 0 && (
          <>
            {/* 슬라이더와 툴팁 */}
            <div
              style={{ width: '100%', position: 'relative', padding: '40px', paddingBottom: '0' }}
            >
              {/* 현재 위치 툴팁 (슬라이더 thumb 위에 위치) */}
              <div
                style={{
                  position: 'absolute',
                  top: '0',
                  left: `calc(40px + (100% - 80px) * ${currentIndex / (monitoringData.length - 1)})`,
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  minWidth: '80px',
                  transition: 'left 0.1s ease-out',
                  pointerEvents: 'none',
                }}
              >
                {/* 툴팁 박스 */}
                <div
                  style={{
                    display: 'flex',
                    padding: `${theme.spacing[2]}px ${theme.spacing[3]}px`,
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: theme.spacing[1],
                    borderRadius: theme.borderRadius.sm,
                    backgroundColor: theme.colors.surface.group.brand,
                  }}
                >
                  <Text size="sm" weight="semibold">
                    {formatTime(currentTime)}
                  </Text>
                  <Text size="xs" style={{ color: theme.colors.text.secondary }}>
                    ({currentIndex + 1}/{monitoringData.length})
                  </Text>
                </div>

                {/* 역삼각형 (아래를 향하는 화살표) */}
                <div
                  style={{
                    width: 0,
                    height: 0,
                    borderLeft: '6px solid transparent',
                    borderRight: '6px solid transparent',
                    borderTop: `6px solid ${theme.colors.surface.group.brand}`,
                    marginTop: '-1px',
                  }}
                />
              </div>

              {/* 슬라이더 입력 */}
              <input
                type="range"
                min={0}
                max={monitoringData.length - 1}
                value={currentIndex}
                onChange={handleSliderChange}
                style={{
                  width: '100%',
                  accentColor: theme.colors.control.timetrack.progress,
                  outline: 'none',
                  background: 'transparent',
                }}
                onFocus={e => {
                  e.target.style.background = 'transparent';
                }}
                onBlur={e => {
                  e.target.style.background = 'transparent';
                }}
              />
            </div>

            {/* 시작 시간 / 컨트롤 버튼 / 끝 시간 */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                alignSelf: 'stretch',
              }}
            >
              <Text size="xs" style={{ color: theme.colors.text.secondary }}>
                {formatTime(new Date(minTime))}
              </Text>

              {/* 재생 컨트롤 버튼 */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  gap: theme.spacing[5],
                  alignSelf: 'stretch',
                }}
              >
                <ButtonTimeLineGroup>
                  <ButtonTimeLine
                    type="backward"
                    onClick={handlePrevious}
                    disabled={currentIndex === 0}
                  />
                  <ButtonTimeLine type={isPlaying ? 'pause' : 'play'} onClick={togglePlayPause} />
                  <ButtonTimeLine
                    type="forward"
                    onClick={handleNext}
                    disabled={currentIndex === monitoringData.length - 1}
                  />
                  <ButtonTimeLine
                    type="speed"
                    speedValue={playbackSpeed as 0.5 | 1 | 2 | 4}
                    onClick={handleSpeedChange}
                  />
                </ButtonTimeLineGroup>
              </div>

              <Text size="xs" style={{ color: theme.colors.text.secondary }}>
                {formatTime(new Date(maxTime))}
              </Text>
            </div>
          </>
        )}

        {/* 데이터가 없을 때 */}
        {(!monitoringData || monitoringData.length === 0) && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: theme.spacing[8],
            }}
          >
            <Text size="sm" style={{ color: theme.colors.text.secondary }}>
              No data available
            </Text>
          </div>
        )}
      </Flex>
    </Card>
  );
};
